/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Avatar,
  Box,
  HStack,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { setAccessToken } from '@partiu/api/axios.ts';
import { IUser } from '@partiu/api/types';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { FaPowerOff } from 'react-icons/fa6';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileProvider.tsx';
import { setBrowserThemeColor } from '../../utils.ts';
import { SectionTitle } from '../atoms/Title.tsx';
import { IMenuLink } from './DrawerMenu.tsx';
import { FaChevronLeft } from 'react-icons/fa';
import { useTheme } from '../../../../sites/club/src/contexts/ThemeProvider.tsx';

function MenuLink({ link }: { link: IMenuLink }) {
  const { colors } = useTheme();
  const isActive = useMemo(() => window.location.pathname === link.path, [link]);

  return (
    <Link to={link.path}>
      <HStack
        key={link.path}
        color={isActive ? colors.primaryColor : 'inherit'}
        transition={'0.2s'}
        _hover={{ color: colors.primaryColor }}
      >
        {link.icon}
        <Text fontSize={'15px'} fontWeight={500}>
          {link.display}
        </Text>
      </HStack>
    </Link>
  );
}

export function TopMenu({
  title = '',
  subTitle = '',
  links,
  children,
  colors: colorsProp,
  backgroundColor = 'primaryColor',
  color = 'primaryFontColor',
  activeColor = 'primaryFontColor',
  logo,
  goBack,
  logoWidth = '20px',
  menuHeight = '50px',
  forceBrowserThemeColor,
  homePage = '/',
  noShadow = false,
  boxShadow,
  logoCenter = false,
  avatarComponent,
  isNative,
}: {
  title?: string;
  subTitle?: string;
  links?: IMenuLink[];
  logoCenter?: boolean;
  colors: any;
  backgroundColor?: string;
  color?: string;
  activeColor?: string;
  goBack?: () => void;
  logo?: string;
  logoWidth?: string;
  menuHeight?: string;
  forceBrowserThemeColor?: string;
  homePage?: string;
  noShadow?: boolean;
  boxShadow?: string;
  isNative?: boolean;
  avatarComponent?: JSX.Element;
} & PropsWithChildren) {
  const colorsAux = useTheme().colors;
  const colors = colorsProp || colorsAux;
  const { resetTheme } = useTheme();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setBrowserThemeColor(forceBrowserThemeColor || '#FFFFFF');
  }, [colors]);

  return (
    <VStack spacing={0} align={'center'} justify={'center'} width={'100%'} position={'fixed'} top={0} zIndex={10}>
      <HStack
        paddingTop={'0px'}
        backgroundColor={backgroundColor}
        backdropFilter={'blur(10px)'}
        boxShadow={!noShadow ? 'rgba(17, 12, 10, 0.1) 0px 30px 50px 0px' : 'none'}
        color={color}
        padding={'0px 30px'}
        height={menuHeight}
        width={'100%'}
        maxWidth={{
          base: '100%',
          sm: '100%',
          lg: '1264px',
        }}
        position={'relative'}
        spacing={2}
        zIndex={11}
      >
        {goBack && (
          <HStack
            padding={isNative ? '0px' : '10px'}
            zIndex={999}
            display={{ base: 'flex', md: 'none' }}
            onClick={() => {
              goBack();
            }}
          >
            <FaChevronLeft />
          </HStack>
        )}
        {logoCenter ? (
          <Box
            position={{
              base: 'absolute',
              md: 'relative',
            }}
            width={{
              base: '85%',
              md: 'auto',
            }}
          >
            <Link to={homePage}>
              <Image
                margin={{
                  base: '0 auto',
                  md: '0',
                }}
                height={logoWidth}
                src={logo || 'https://partiu-static.s3.amazonaws.com/partiu_logo_black_white.png'}
              />
            </Link>
          </Box>
        ) : (
          <Link to={homePage}>
            <Image
              height={logoWidth}
              src={logo || 'https://partiu-static.s3.amazonaws.com/partiu_logo_black_white.png'}
            />
          </Link>
        )}
        <SectionTitle display={{ base: 'none', md: 'block' }} fontSize={'12pt'}>
          {title + ((subTitle && ' - ' + subTitle) || '')}
        </SectionTitle>
        <Spacer />
        <HStack paddingRight={'20px'} spacing={8} display={{ base: 'none', lg: 'flex' }}>
          {links &&
            links
              .filter((l) => l.showOnTopMenu)
              .map((l) => {
                return <MenuLink key={l.path} link={l} />;
              })}
        </HStack>
        {children}
        {profile &&
          (avatarComponent ? (
            avatarComponent
          ) : (
            <Menu>
              <MenuButton cursor={'pointer'} as={Avatar} src={profile.image} boxSize={'36px'} />
              <MenuList>
                <MenuItem
                  color={'black'}
                  onClick={() => {
                    resetTheme();
                    setAccessToken(null);
                    navigate('/');
                    window.location.reload();
                  }}
                  icon={<FaPowerOff />}
                >
                  Sair
                </MenuItem>
              </MenuList>
            </Menu>
          ))}
      </HStack>
      {links && links.filter((l) => l.showOnQuickMenu).length > 0 && (
        <HStack
          paddingRight={'20px'}
          backgroundColor={backgroundColor}
          id={'quick-menu'}
          color={color}
          width="100%"
          justify={'flex-start'}
          height={'45px'}
          spacing={0}
          boxShadow={!noShadow && `${boxShadow || 'var(--chakra-colors-primaryColor)'} 0px 0px 5px`}
          overflowX="scroll"
          className="hide-scrollbar"
          zIndex={10}
        >
          {links
            ?.filter((l) => l.showOnQuickMenu === undefined || l.showOnQuickMenu)
            .filter((l) => l.canShow === undefined || (l.canShow && l.canShow(data as IUser)))
            .map((l) => (
              <Link key={l.path} style={{ height: '100%' }} to={l.path}>
                <HStack
                  height={'100%'}
                  padding={'0px 20px'}
                  paddingTop={'2px'}
                  transition={'0.5s'}
                  spacing={4}
                  borderBottom={location.pathname === l.path ? `3px solid ${activeColor}` : '3px solid transparent'}
                  color={location.pathname === l.path ? activeColor : color}
                >
                  {l.icon}
                  <Text whiteSpace={'nowrap'} fontWeight={600} fontSize={16}>
                    {l.display}
                  </Text>
                </HStack>
              </Link>
            ))}
        </HStack>
      )}
    </VStack>
  );
}
